import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useApolloClient, useQuery } from '@apollo/client/react'
import { getShortLanguage } from '@utils/language'
import { ErrorResponse, User as OidcUser } from 'oidc-client-ts'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Cookies } from 'react-cookie'
import { toast } from 'react-toastify'

import { clearPersistedCache } from '@/apollo/main/cachePersist'
import {
  authOidcDebugLog,
  authOidcSerializeError,
  authOidcSilentRenewErrorMayBenefitFromIframeFallback,
  authOidcUserSnapshotMeta,
} from '@/auth/authOidcDebugLog'
import {
  AUTH_SESSION_INVALIDATED_STORAGE_KEY,
  broadcastAuthSessionInvalidatedAcrossTabs,
} from '@/auth/authSessionCrossTab'
import {
  notifyAuthSessionLost,
  registerAuthSessionLostHandler,
} from '@/auth/authSessionLost'
import {
  clearBrowserImpersonationSession,
  clearImpersonationAdminExitPending,
  configureSilentRenewForOidcUser,
  hasBrowserImpersonationSessionFlag,
  isExchangeIssuedAccessToken,
  markBrowserImpersonationSession,
  shouldUseExchangeBackendRefresh,
  tryRenewExchangeSession,
} from '@/auth/keycloakExchangeSession'
import { signinWithPasswordViaBridge } from '@/auth/keycloakLegacyBridgeLogin'
import {
  getKeycloakUserManager,
  signinResourceOwnerCredentialsWithMergedExtra,
} from '@/auth/keycloakUserManager'
import { isLikelyAuthInfrastructureFailure } from '@/auth/loginInfrastructureErrors'
import { clearPreImpersonationOidcSnapshot } from '@/auth/preImpersonationOidcSnapshot'
import { beginSsoRedirect } from '@/auth/ssoRedirect'
import { env, isProd, isTenantHost } from '@/config/env'
import { useSwitchContextState } from '@/contexts/SwitchContextContext'
import {
  AuthUser__UserFragment as User,
  AuthUserDocument,
  SwitchContextDocument,
} from '@/graphql/api/generated'
import { useContextSwitchDispatcher } from '@/hooks/useContextSwitchDispatcher'
import { useMercureContextUpdates } from '@/hooks/useMercureContextUpdates'
import { useMercureSubscribeToken } from '@/hooks/useMercureSubscribeToken'
import { UserRoutes } from '@/routes/_hooks/routes.enum'
import { useNavigate } from '@/routing'
import { SentryService, TrackingService } from '@/services/tracking'
import i18n from '@/translations'
import { emitContextChanged } from '@/utils/contextChangedEvent'

import { oidcUserFromKeycloakTokens } from './keycloakLegacyBridgeLogin'
import { refreshWithPublicClient } from './refreshWithPublicClient'
import { consumeHandoffOnArrival } from './sessionHandoff'

export interface AuthContextType {
  user: User | null
  /** Keycloak access token (for rare direct fetch calls). Prefer Apollo for API. */
  token: string | null
  loading: boolean
  /** Re-read OIDC session + refetch `currentUser` (after impersonation / restore). */
  refreshSession: () => Promise<void>
  /** Push the current OIDC UserManager access token into React state (after signinSilent outside this provider). */
  syncOidcAccessToken: () => Promise<void>
  /** Clear OIDC user + React auth cookie locally without Keycloak logout redirect (e.g. before in-app re-login). */
  clearLocalOidcSession: () => Promise<void>
  /** Email/password: Keycloak ROPC first; on invalid_grant, API legacy bridge (then DB password cleared). `otp`: 6-digit TOTP when 2FA is enabled (Keycloak direct grant). */
  signinWithPassword: (
    email: string,
    password: string,
    otp?: string
  ) => Promise<void>
  /** Interactive SSO login brokered to an identity provider (Google / per-network IdP) via kc_idp_hint. */
  signinWithProvider: (
    idpHint: string,
    opts?: { returnUrl?: string; loginHint?: string }
  ) => Promise<void>
  signin: (response: User, callback?: () => void) => void
  signout: (callback?: () => void) => Promise<void>
  /** True when JWT was issued via admin token-exchange (impersonation). */
  isImpersonationExchangeSession: boolean
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  loading: false,
  refreshSession: async () => {},
  syncOidcAccessToken: async () => {},
  clearLocalOidcSession: async () => {},
  signinWithPassword: async () => {},
  signinWithProvider: async () => {},
  signin: () => {},
  signout: async () => {},
  isImpersonationExchangeSession: false,
})

const syncTokenFromOidc = async (
  setToken: React.Dispatch<React.SetStateAction<string | null>>
): Promise<void> => {
  const um = getKeycloakUserManager()
  const u = await um.getUser()
  setToken(u?.access_token ?? null)
}

/**
 * Local dev is http://localhost — browsers reject Secure cookies over HTTP.
 *
 * `COOKIE_DOMAIN` is the platform's registrable domain, so that its subdomains share a
 * session. It must not be sent from a dedicated space: on a customer-owned host the browser
 * discards the whole cookie as a cross-domain attempt — `Domain=.demo.communo.app` from
 * `partage.ma-ville.fr` — and the owner comes back to a session the app cannot see.
 *
 * Omitting the attribute makes the cookie host-only, which is also the right scope for a
 * dedicated space on one of our own subdomains: its session has no business being readable
 * by the platform or by the space next door.
 */
const userAuthCookieOptions = () => ({
  path: '/',
  sameSite: 'lax' as const,
  domain: isTenantHost() ? undefined : env.COOKIE_DOMAIN,
  secure: isProd,
})

const SILENT_RENEW_IFRAME_FALLBACK_COOLDOWN_MS = 45_000

function authUserStabilityFingerprint(user: User | null | undefined): string {
  if (!user) {
    return 'null'
  }

  return JSON.stringify({
    id: user.id,
    slug: user.slug,
    firstname: user.firstname ?? null,
    lastname: user.lastname ?? null,
    fullname: user.fullname,
    email: user.email,
    roles: user.roles ?? null,
    confirmed: user.confirmed,
    authorizedContextsTree: user.authorizedContextsTree,
    averageRatingScore: user.averageRatingScore ?? null,
    avatarId: user.avatar?.id ?? null,
    avatarUrl: user.avatar?.contentUrl ?? null,
    settingsId: user.settings?.id ?? null,
    language: user.settings?.language ?? null,
    currencySymbol: user.settings?.currencySymbol ?? null,
    themeMode: user.settings?.themeMode ?? null,
    useAbbreviatedName: user.settings?.useAbbreviatedName ?? null,
    preferedCommunicationMode: user.settings?.preferedCommunicationMode ?? null,
    circleMembershipsTotalCount: user.circleMemberships?.totalCount ?? null,
    // Status + permission digest so the profile-onboarding gate reacts to a
    // membership going pending→ok or gaining Owner/Administrator even when the
    // total count is unchanged (otherwise setUser early-returns and the gate
    // never lifts).
    circleMembershipsActivation: (user.circleMemberships?.edges ?? [])
      .map(
        (edge) => `${edge?.node?.status ?? ''}:${edge?.node?.permission ?? ''}`
      )
      .sort()
      .join(','),
    networkMembershipsTotalCount: user.networkMemberships?.totalCount ?? null,
    networkMembershipsActivation: (user.networkMemberships?.edges ?? [])
      .map(
        (edge) => `${edge?.node?.status ?? ''}:${edge?.node?.permission ?? ''}`
      )
      .sort()
      .join(','),
    ratingsTotalCount: user.ratings?.totalCount ?? null,
    insuranceCertificateDocumentId:
      user.insuranceCertificateDocument?.id ?? null,
    insuranceCertificateValidatedAt:
      user.insuranceCertificateDocument?.validatedAt ?? null,
    insuranceCertificateExpiresAt:
      user.insuranceCertificateDocument?.expiresAt ?? null,
    lastContextIri: user.lastContextIri ?? null,
    lastContextName: user.lastContextName ?? null,
  })
}

interface SwitchContextToastProps {
  firstname: string
  contextType: 'network' | 'circle'
  contextName: string
  onSwitch: () => Promise<void>
  closeToast?: () => void
}

const SwitchContextToast = ({
  firstname,
  contextType,
  contextName,
  onSwitch,
  closeToast,
}: SwitchContextToastProps) => {
  const handleClick = () => {
    void onSwitch()
    if (closeToast) {
      closeToast()
    }
  }

  const greetingText = i18n.t('toasts:contextRestoration.greeting', {
    firstname,
  })
  const typeLabel = i18n.t(
    `toasts:contextRestoration.type${contextType === 'circle' ? 'Circle' : 'Network'}`
  )
  const welcomeBackText = i18n.t('toasts:contextRestoration.welcomeBack', {
    type: typeLabel,
    name: contextName,
  })
  const actionText = i18n.t('toasts:contextRestoration.actionLink')
  const orUseMenuText = i18n.t('toasts:contextRestoration.orUseMenu')

  return (
    <div>
      <div>
        {greetingText}
        <br />
        {welcomeBackText}
      </div>
      <div style={{ marginTop: '8px' }}>
        <button
          onClick={handleClick}
          style={{
            background: 'none',
            border: 'none',
            color: '#3182ce',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit',
            fontWeight: 'bold',
          }}
        >
          {actionText}
        </button>
        {orUseMenuText}
      </div>
    </div>
  )
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { setImpersonationExitOverlay } = useSwitchContextState()
  const { dispatchContextSwitchNavigation } = useContextSwitchDispatcher()
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  /** Impersonation UI: JWT `azp` can be missing after some refreshes; keep menu flag via sessionStorage. */
  const [isImpersonationUiActive, setIsImpersonationUiActive] = useState(
    () => typeof window !== 'undefined' && hasBrowserImpersonationSessionFlag()
  )
  const [oidcReady, setOidcReady] = useState(false)
  // Snapshot the access_token cookie during component initialization, before any
  // React effect runs. The token-cleanup effect (below) removes the cookie when
  // token=null, which happens before the OIDC-init effect — so by the time the
  // init async IIFE reads document.cookie, the cookie is already gone. We must
  // capture it here, synchronously, while it still exists.
  const hadAccessTokenCookieAtMountRef = useRef(
    typeof document !== 'undefined' &&
      document.cookie
        .split(';')
        .some((c) => c.trim().startsWith('access_token='))
  )
  // AuthProvider only WRITES the `user` / `access_token` cookies (it never reads
  // their values), so it must NOT subscribe to cookie changes via `useCookies`:
  // react-cookie's change listener fires a setState, and when a cookie changes
  // mid-navigation that setState lands during the Next Router's render, which
  // interrupts the navigation transition (e.g. the post-fork redirect never
  // commits) and logs a "setState while rendering Router" error. A standalone
  // Cookies instance writes to document.cookie with the same API, no listener.
  const writeCookies = useMemo(() => new Cookies(), [])
  const setCookie = useMemo(
    () => writeCookies.set.bind(writeCookies),
    [writeCookies]
  )
  const removeCookie = useMemo(
    () => writeCookies.remove.bind(writeCookies),
    [writeCookies]
  )
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const silentRenewIframeFallbackNotBeforeRef = useRef(0)
  const lastVisibilityTokenRefreshRef = useRef(0)
  const lastAppliedAuthUserFingerprintRef = useRef<string>('null')
  const hasRestoredContextRef = useRef(false)

  const {
    loading: authUserQueryLoading,
    refetch,
    data: authUserData,
    error: authUserQueryError,
  } = useQuery(AuthUserDocument, {
    skip: !token,
    // First request may hit network; later automatic reobserves use cache-only to avoid
    // authUser ↔ material queries ping-pong when both normalize the same User/Circle.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-only',
    // Keep currentUser when nested fields fail (e.g. normalization on one branch).
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
  })

  useEffect(() => {
    if (!token) {
      lastAppliedAuthUserFingerprintRef.current = 'null'
      hasRestoredContextRef.current = false
      return
    }
    const u = authUserData?.currentUser
    if (!u) {
      return
    }
    const nextFingerprint = authUserStabilityFingerprint(u)
    if (lastAppliedAuthUserFingerprintRef.current === nextFingerprint) {
      return
    }
    lastAppliedAuthUserFingerprintRef.current = nextFingerprint
    setUser(u)
    // Identify the restored session to the tracking stack too. Without this, only the
    // explicit login path (signin) ever identifies: a user who reloads with a stored token
    // stays anonymous to PostHog, and every feature flag targeting a person property
    // (email, …) evaluates to "no matching condition" for them — the flag looks broken
    // precisely for the people it targets. No `user_signed_in` event here: restoring a
    // session is not signing in. PostHog is initialised at provider mount, well before
    // this effect's GraphQL roundtrip resolves.
    TrackingService.setUser(u)

    if (typeof window !== 'undefined' && !hasRestoredContextRef.current) {
      hasRestoredContextRef.current = true
      const storedIri = localStorage.getItem('contextIri')
      if (storedIri === null && u.lastContextIri) {
        const isCircle = u.lastContextIri.includes('/circles/')
        const isNetwork = u.lastContextIri.includes('/networks/')
        if (isCircle || isNetwork) {
          localStorage.setItem('contextIri', u.lastContextIri)
          if (u.lastContextName) {
            localStorage.setItem('contextName', u.lastContextName)
          } else {
            localStorage.removeItem('contextName')
          }
          emitContextChanged('AuthProvider:restore')

          const type = isCircle ? 'circle' : 'network'
          const handleSwitchBack = async () => {
            try {
              const result = await apolloClient.mutate({
                mutation: SwitchContextDocument,
                variables: { input: { contextIri: undefined } },
              })
              const output = result.data?.switchContextUser?.user
              if (output) {
                if (output.requiresTokenRefresh) {
                  const um = getKeycloakUserManager()
                  try {
                    const sessionUser = await um.getUser()
                    if (shouldUseExchangeBackendRefresh(sessionUser)) {
                      await tryRenewExchangeSession(um)
                    } else {
                      await um.signinSilent()
                    }
                    await syncTokenFromOidc(setToken)
                  } catch (tokenErr) {
                    console.error(
                      'Failed to refresh token in toast switchback:',
                      tokenErr
                    )
                  }
                }
                localStorage.removeItem('contextIri')
                if (output.contextName) {
                  localStorage.setItem('contextName', output.contextName)
                } else {
                  localStorage.removeItem('contextName')
                }
                emitContextChanged('AuthProvider:restore:switchToUser')
                await apolloClient.clearStore()
                dispatchContextSwitchNavigation(null)
                toast.success(i18n.t('toasts:contextRestoration.switchSuccess'))
              }
            } catch (err) {
              console.error(
                'Error switching to individual context from toast:',
                err
              )
              toast.error(
                i18n.t('context.switchError') || 'Error switching context'
              )
            }
          }

          toast.info(
            <SwitchContextToast
              firstname={u.firstname ?? ''}
              contextType={type}
              contextName={u.lastContextName ?? ''}
              onSwitch={handleSwitchBack}
            />,
            {
              autoClose: 10000,
            }
          )
        }
      }
    }

    setCookie(
      'user',
      {
        id: u.id,
        slug: u.slug,
        fullname: u.fullname,
        avatar: u.avatar?.contentUrl ?? null,
      },
      userAuthCookieOptions()
    )
  }, [
    token,
    authUserData?.currentUser,
    setCookie,
    apolloClient,
    dispatchContextSwitchNavigation,
  ])

  useEffect(() => {
    if (token) {
      setCookie('access_token', token, userAuthCookieOptions())
    } else {
      removeCookie('access_token', userAuthCookieOptions())
    }
  }, [token, setCookie, removeCookie])

  const { token: mercureToken, topics: mercureTopics } =
    useMercureSubscribeToken(token)
  const mercureContextTopic = useMemo(() => {
    if (!user?.slug) {
      return null
    }
    return (
      mercureTopics.find((topic) =>
        topic.endsWith(`/users/${user.slug}/context`)
      ) ?? null
    )
  }, [mercureTopics, user?.slug])

  useMercureContextUpdates({
    mercureToken,
    topic: mercureContextTopic,
    enabled: Boolean(token && user?.slug),
  })

  useEffect(() => {
    if (!authUserQueryError || !authUserData?.currentUser) {
      return
    }
    const messages = CombinedGraphQLErrors.is(authUserQueryError)
      ? authUserQueryError.errors.map((e) => e.message)
      : authUserQueryError instanceof Error
        ? [authUserQueryError.message]
        : []
    const sentryMessage =
      '[auth] currentUser query returned partial data (some fields errored).'
    console.warn(sentryMessage, messages)
    SentryService.captureWarning(sentryMessage, {
      graphQLErrorMessages: messages,
      operation: 'authUser',
    })
  }, [authUserQueryError, authUserData?.currentUser])

  useEffect(() => {
    if (token === null) {
      if (!hasBrowserImpersonationSessionFlag()) {
        setIsImpersonationUiActive(false)
      }
      return
    }
    const fromJwt = isExchangeIssuedAccessToken(token)
    if (fromJwt) {
      markBrowserImpersonationSession()
      setIsImpersonationUiActive(true)
      return
    }
    if (hasBrowserImpersonationSessionFlag()) {
      setIsImpersonationUiActive(true)
      return
    }
    clearBrowserImpersonationSession()
    setIsImpersonationUiActive(false)
  }, [token])

  useEffect(() => {
    TrackingService.init()
  }, [])

  useEffect(() => {
    const unregisterSessionLost = registerAuthSessionLostHandler(async () => {
      authOidcDebugLog('session_lost_handler_run_clearing_app_session')
      clearBrowserImpersonationSession()
      clearImpersonationAdminExitPending()
      setImpersonationExitOverlay(false)
      clearPreImpersonationOidcSnapshot()
      const um = getKeycloakUserManager()
      await um.removeUser().catch(() => {})
      setToken(null)
      setUser(null)
      removeCookie('user', userAuthCookieOptions())
      localStorage.removeItem('communo_original_user')
      localStorage.removeItem('contextIri')
      localStorage.removeItem('contextName')
      localStorage.removeItem('switchingTo')
      TrackingService.reset()
      broadcastAuthSessionInvalidatedAcrossTabs()
      toast.error(i18n.t('toasts:auth.sessionExpired'), { autoClose: 8000 })
      navigate(UserRoutes.Login, { replace: true })
    })

    const onStorageSessionInvalidated = (e: StorageEvent) => {
      if (
        e.key !== AUTH_SESSION_INVALIDATED_STORAGE_KEY ||
        e.newValue === null
      ) {
        return
      }
      authOidcDebugLog('auth_cross_tab_session_invalidated', {
        storageValue: e.newValue,
      })
      void (async () => {
        clearBrowserImpersonationSession()
        clearImpersonationAdminExitPending()
        setImpersonationExitOverlay(false)
        clearPreImpersonationOidcSnapshot()
        const um = getKeycloakUserManager()
        await um.removeUser().catch(() => {})
        setToken(null)
        setUser(null)
        removeCookie('user', userAuthCookieOptions())
        localStorage.removeItem('communo_original_user')
        localStorage.removeItem('contextIri')
        localStorage.removeItem('contextName')
        localStorage.removeItem('switchingTo')
        TrackingService.reset()
        navigate(UserRoutes.Login, { replace: true })
      })()
    }
    window.addEventListener('storage', onStorageSessionInvalidated)

    const um = getKeycloakUserManager()

    // A hand-off, if this page was opened with one. Runs before anything concludes that nobody
    // is signed in here: the whole point is that the session exists, it just lives on the other
    // side of an origin boundary the product draws on purpose.
    void consumeHandoffOnArrival(async (refreshToken) => {
      try {
        const tokens = await refreshWithPublicClient(refreshToken)

        if (!tokens) {
          return false
        }

        const next = oidcUserFromKeycloakTokens(tokens)
        await um.storeUser(next)
        await um.events.load(next)

        return true
      } catch {
        // A failed hand-off costs a login, never the crossing.
        return false
      }
    })

    const onLoaded = () => {
      authOidcDebugLog('oidc_event_user_loaded')
      void syncTokenFromOidc(setToken)
      void um.getUser().then((loaded) => {
        configureSilentRenewForOidcUser(loaded)
      })
    }
    um.events.addUserLoaded(onLoaded)
    um.events.addUserUnloaded(() => {
      authOidcDebugLog('oidc_event_user_unloaded')
      setToken(null)
      setUser(null)
    })
    const unsubAccessTokenExpiring = um.events.addAccessTokenExpiring(() => {
      void um.getUser().then((u) => {
        authOidcDebugLog('oidc_event_access_token_expiring', {
          ...authOidcUserSnapshotMeta(u),
        })
      })
    })
    const unsubAccessTokenExpired = um.events.addAccessTokenExpired(() => {
      void um.getUser().then((u) => {
        authOidcDebugLog('oidc_event_access_token_expired', {
          ...authOidcUserSnapshotMeta(u),
        })
      })
    })
    const unsubSilentRenewError = um.events.addSilentRenewError(
      async (error) => {
        authOidcDebugLog(
          'oidc_event_silent_renew_error',
          authOidcSerializeError(error)
        )
        let u: OidcUser | null = null
        try {
          u = await um.getUser()
        } catch {
          authOidcDebugLog('oidc_silent_renew_error_getUser_failed')
          notifyAuthSessionLost('silent_renew_error_getUser_failed')
          return
        }
        if (!u?.expired) {
          const now = Date.now()
          if (
            authOidcSilentRenewErrorMayBenefitFromIframeFallback(error) &&
            now >= silentRenewIframeFallbackNotBeforeRef.current
          ) {
            silentRenewIframeFallbackNotBeforeRef.current =
              now + SILENT_RENEW_IFRAME_FALLBACK_COOLDOWN_MS
            authOidcDebugLog('oidc_silent_renew_attempt_iframe_fallback', {
              cooldownMs: SILENT_RENEW_IFRAME_FALLBACK_COOLDOWN_MS,
              ...authOidcUserSnapshotMeta(u),
            })
            try {
              const renewed = await um.signinSilent({ forceIframeAuth: true })
              if (renewed?.access_token) {
                authOidcDebugLog('oidc_silent_renew_iframe_fallback_ok', {
                  ...authOidcUserSnapshotMeta(renewed),
                })
                setToken(renewed.access_token)
                silentRenewIframeFallbackNotBeforeRef.current = 0
              } else {
                authOidcDebugLog('oidc_silent_renew_iframe_fallback_empty', {
                  ...authOidcUserSnapshotMeta(renewed),
                })
              }
            } catch (e2) {
              authOidcDebugLog(
                'oidc_silent_renew_iframe_fallback_failed',
                authOidcSerializeError(e2)
              )
            }
            return
          }

          authOidcDebugLog('oidc_silent_renew_error_ignored_user_not_expired', {
            ...authOidcUserSnapshotMeta(u),
          })
          return
        }
        if (shouldUseExchangeBackendRefresh(u)) {
          try {
            const renewed = await tryRenewExchangeSession(um)
            if (renewed?.access_token && !renewed.expired) {
              authOidcDebugLog('oidc_silent_renew_exchange_recovery_ok', {
                ...authOidcUserSnapshotMeta(renewed),
              })
              setToken(renewed.access_token)
              return
            }
          } catch (e) {
            authOidcDebugLog(
              'oidc_silent_renew_exchange_recovery_failed',
              authOidcSerializeError(e)
            )
          }
        }
        notifyAuthSessionLost('silent_renew_error_user_still_expired', {
          ...authOidcUserSnapshotMeta(u),
        })
      }
    )

    void (async () => {
      const initial = await um.getUser().catch(() => null)

      let resolved = initial
      const needsSilentRenew =
        // No OIDC session in localStorage at all
        !initial?.access_token ||
        // OIDC session exists but access token is already expired
        initial.expired === true
      if (
        needsSilentRenew &&
        hadAccessTokenCookieAtMountRef.current &&
        // Never attempt signinSilent when running inside a silent-renew iframe
        // (the OIDC callback page also mounts AuthProvider). Without this guard
        // the iframe would spawn another iframe, causing recursive nesting.
        window.self === window.top
      ) {
        authOidcDebugLog('oidc_init_cookie_hint_silent_renew_start', {
          reason: !initial?.access_token ? 'no_token' : 'expired',
        })
        try {
          const silentUser = await um.signinSilent()
          if (silentUser?.access_token) {
            authOidcDebugLog(
              'oidc_init_cookie_hint_silent_renew_ok',
              authOidcUserSnapshotMeta(silentUser)
            )
            resolved = silentUser
          } else if (initial?.expired) {
            // Renewal returned no token — clear the expired session
            resolved = null
          }
        } catch (e) {
          authOidcDebugLog(
            'oidc_init_cookie_hint_silent_renew_failed',
            authOidcSerializeError(e)
          )
          // If the token was expired and renewal failed, clear it so HomeView
          // doesn't briefly render with a stale token before the session-lost
          // handler fires.
          if (initial?.expired) {
            resolved = null
          }
        }
      }

      setToken(resolved?.access_token ?? null)
      configureSilentRenewForOidcUser(resolved)
      setOidcReady(true)
    })()

    return () => {
      unregisterSessionLost()
      unsubSilentRenewError()
      unsubAccessTokenExpiring()
      unsubAccessTokenExpired()
      um.events.removeUserLoaded(onLoaded)
      window.removeEventListener('storage', onStorageSessionInvalidated)
    }
  }, [navigate, removeCookie, setImpersonationExitOverlay])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const t = Date.now()
      if (t - lastVisibilityTokenRefreshRef.current < 15_000) return
      lastVisibilityTokenRefreshRef.current = t
      void (async () => {
        const um = getKeycloakUserManager()
        let u: OidcUser | null = null
        try {
          u = await um.getUser()
        } catch {
          return
        }
        if (!u?.access_token || u.expired) return
        const exp = u.expires_at
        if (typeof exp !== 'number') return
        const left = exp - Math.floor(Date.now() / 1000)
        if (left > 180 || left < -10) return
        authOidcDebugLog('document_visible_proactive_token_refresh', {
          expiresInSec: left,
        })
        try {
          if (shouldUseExchangeBackendRefresh(u)) {
            await tryRenewExchangeSession(um)
          } else {
            await um.signinSilent()
          }
          await syncTokenFromOidc(setToken)
          authOidcDebugLog('document_visible_proactive_token_refresh_ok', {})
        } catch (e) {
          authOidcDebugLog(
            'document_visible_proactive_token_refresh_failed',
            authOidcSerializeError(e)
          )
        }
      })()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [setToken])

  useEffect(() => {
    if (user?.settings?.language) {
      i18n.changeLanguage(getShortLanguage(user.settings.language))
    }
  }, [user])

  const refetchAuthUserRef = useRef(refetch)
  refetchAuthUserRef.current = refetch
  const previousAccessTokenRef = useRef<string | null>(null)

  // Refetch currentUser only when the JWT changes (impersonation, refresh, etc.).
  // Do not depend on `refetch`: Apollo may renew its reference → infinite authUser + other queries loop.
  // On first token, useQuery(AuthUserDocument, skip: false) already triggers a fetch.
  useEffect(() => {
    if (!token) {
      previousAccessTokenRef.current = null
      return
    }
    const prev = previousAccessTokenRef.current
    previousAccessTokenRef.current = token
    if (prev !== null && prev !== token) {
      void refetchAuthUserRef.current()
    }
  }, [token])

  const refreshSession = useCallback(async () => {
    const um = getKeycloakUserManager()
    let u = await um.getUser()
    authOidcDebugLog('refresh_session_start', {
      ...authOidcUserSnapshotMeta(u),
    })
    if (u?.expired) {
      if (shouldUseExchangeBackendRefresh(u)) {
        try {
          const renewed = await tryRenewExchangeSession(um)
          if (renewed && !renewed.expired) {
            u = renewed
            authOidcDebugLog('refresh_session_exchange_refresh_ok', {
              ...authOidcUserSnapshotMeta(u),
            })
          }
        } catch (e) {
          authOidcDebugLog(
            'refresh_session_exchange_refresh_failed',
            authOidcSerializeError(e)
          )
        }
      }
    }
    if (u?.expired) {
      try {
        u = await um.signinSilent()
        authOidcDebugLog('refresh_session_signinSilent_ok', {
          ...authOidcUserSnapshotMeta(u),
        })
      } catch (e) {
        authOidcDebugLog(
          'refresh_session_signinSilent_failed',
          authOidcSerializeError(e)
        )
        if (authOidcSilentRenewErrorMayBenefitFromIframeFallback(e)) {
          try {
            u = await um.signinSilent({ forceIframeAuth: true })
            if (u?.access_token) {
              authOidcDebugLog('refresh_session_iframe_fallback_ok', {
                ...authOidcUserSnapshotMeta(u),
              })
            } else {
              notifyAuthSessionLost('refreshSession_signinSilent_failed')
              return
            }
          } catch (e2) {
            authOidcDebugLog(
              'refresh_session_iframe_fallback_failed',
              authOidcSerializeError(e2)
            )
            notifyAuthSessionLost('refreshSession_signinSilent_failed')
            return
          }
        } else {
          notifyAuthSessionLost('refreshSession_signinSilent_failed')
          return
        }
      }
    }
    const nextTok = u?.access_token ?? null
    setToken(nextTok)
    if (!nextTok) {
      setUser(null)
      removeCookie('user', userAuthCookieOptions())
      return
    }
    const { data, error: authUserRefreshError } = await apolloClient.query({
      query: AuthUserDocument,
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    })
    if (
      CombinedGraphQLErrors.is(authUserRefreshError) &&
      authUserRefreshError.errors.length > 0 &&
      data?.currentUser
    ) {
      const messages = authUserRefreshError.errors.map((e) => e.message)
      const sentryMessage = '[auth] refreshSession: partial GraphQL errors.'
      console.warn(sentryMessage, messages)
      SentryService.captureWarning(sentryMessage, {
        graphQLErrorMessages: messages,
        operation: 'authUser',
        source: 'refreshSession',
      })
    }
    if (data?.currentUser) {
      const cu = data.currentUser
      setUser(cu)
      setCookie(
        'user',
        {
          id: cu.id,
          slug: cu.slug,
          fullname: cu.fullname,
          avatar: cu.avatar?.contentUrl ?? null,
        },
        userAuthCookieOptions()
      )
    }
  }, [apolloClient, removeCookie, setCookie])

  const syncOidcAccessToken = useCallback(async () => {
    await syncTokenFromOidc(setToken)
  }, [])

  const clearLocalOidcSession = useCallback(async () => {
    clearBrowserImpersonationSession()
    clearImpersonationAdminExitPending()
    setImpersonationExitOverlay(false)
    clearPreImpersonationOidcSnapshot()
    const um = getKeycloakUserManager()
    await um.removeUser().catch(() => {})
    try {
      await apolloClient.clearStore()
    } catch {
      //
    }
    // Drop the persisted cache snapshot so no user data lingers after logout.
    clearPersistedCache()
    setToken(null)
    setUser(null)
    removeCookie('user', userAuthCookieOptions())
  }, [apolloClient, removeCookie, setImpersonationExitOverlay])

  const signinWithPassword = useCallback(
    async (email: string, password: string, otp?: string) => {
      clearBrowserImpersonationSession()
      clearImpersonationAdminExitPending()
      setImpersonationExitOverlay(false)
      clearPreImpersonationOidcSnapshot()
      // Drop any context left over from a previous session/user before
      // establishing a new one. The backend falls back to the user's
      // persisted context (User.lastContext), so the client never needs to
      // resend a stale contextIri. Without this, a fresh login (incl. the
      // post-registration auto-login) would keep sending another user's
      // X-Communo-Context-Iri header and get AccessDenied responses.
      localStorage.removeItem('contextIri')
      localStorage.removeItem('contextName')
      localStorage.removeItem('switchingTo')
      if (typeof window !== 'undefined') {
        emitContextChanged('AuthProvider.signinWithPassword')
      }
      const um = getKeycloakUserManager()
      const trimmed = email.trim()
      const otpTrimmed = (otp ?? '').replace(/\s+/g, '')
      const extraTokenParams =
        otpTrimmed !== '' && /^\d{6}$/.test(otpTrimmed)
          ? { otp: otpTrimmed }
          : undefined

      const applyOidcSession = async (oidcUser: OidcUser) => {
        setToken(oidcUser.access_token ?? null)
        setOidcReady(true)
      }

      try {
        const oidcUser = await signinResourceOwnerCredentialsWithMergedExtra(
          um,
          {
            username: trimmed,
            password,
            extraTokenParams,
          }
        )
        await applyOidcSession(oidcUser)
        configureSilentRenewForOidcUser(oidcUser)
      } catch (e) {
        const invalidGrant =
          e instanceof ErrorResponse && e.error === 'invalid_grant'

        if (!invalidGrant) {
          if (isLikelyAuthInfrastructureFailure(e)) {
            throw e instanceof Error
              ? e
              : new Error('Login service unavailable.')
          }
          if (e instanceof ErrorResponse) {
            throw new Error(
              e.error_description ??
                e.error ??
                i18n.t(
                  'login.toast.invalidCredentials',
                  'Invalid credentials or account unavailable.'
                )
            )
          }
          throw e instanceof Error
            ? e
            : new Error(
                i18n.t(
                  'login.toast.invalidCredentials',
                  'Invalid credentials or account unavailable.'
                )
              )
        }

        if (extraTokenParams) {
          throw new Error(
            i18n.t(
              'login.toast.otpFailed',
              'Incorrect code or invalid credentials. If your code is correct, click "Back to sign in" to check your password or reset it.'
            )
          )
        }

        try {
          const oidcUser = await signinWithPasswordViaBridge(
            trimmed,
            password,
            {
              skipKeycloakPrecheck: true,
            }
          )
          await applyOidcSession(oidcUser)
          configureSilentRenewForOidcUser(oidcUser)
        } catch (inner) {
          throw inner instanceof Error
            ? inner
            : new Error(
                i18n.t(
                  'login.toast.invalidCredentials',
                  'Invalid credentials or account unavailable.'
                )
              )
        }
      }
    },
    [setImpersonationExitOverlay]
  )

  const signinWithProvider = useCallback(
    async (
      idpHint: string,
      opts?: { returnUrl?: string; loginHint?: string }
    ) => {
      clearBrowserImpersonationSession()
      clearImpersonationAdminExitPending()
      setImpersonationExitOverlay(false)
      clearPreImpersonationOidcSnapshot()
      // Same rationale as signinWithPassword: drop any leftover context before
      // establishing a new session via the redirect.
      localStorage.removeItem('contextIri')
      localStorage.removeItem('contextName')
      localStorage.removeItem('switchingTo')
      if (typeof window !== 'undefined') {
        emitContextChanged('AuthProvider.signinWithProvider')
      }
      // The Apollo cache is persisted to storage; clear both the in-memory
      // store and the persisted snapshot before leaving for Keycloak so no
      // stale user data lingers if the redirect is interrupted.
      try {
        await apolloClient.clearStore()
      } catch {
        // Same rationale as clearLocalOidcSession: a rejected clearStore must
        // not block the SSO redirect from starting.
      }
      clearPersistedCache()
      await beginSsoRedirect({
        idpHint,
        returnUrl: opts?.returnUrl,
        loginHint: opts?.loginHint,
      })
    },
    [apolloClient, setImpersonationExitOverlay]
  )

  const signin = useCallback(
    (response: User, callback?: () => void) => {
      setUser(response)
      localStorage.removeItem('contextIri')
      localStorage.removeItem('contextName')
      if (typeof window !== 'undefined') {
        emitContextChanged('AuthProvider.signin')
      }
      setCookie(
        'user',
        {
          id: response.id,
          slug: response.slug,
          fullname: response.fullname,
          avatar: response.avatar?.contentUrl ?? null,
        },
        userAuthCookieOptions()
      )
      TrackingService.setUser(response)
      TrackingService.captureEvent('user_signed_in', {
        user_id: response.id,
        method: 'session',
      })
      if (callback) {
        callback()
      }
    },
    [setCookie]
  )

  const signout = useCallback(
    async (callback?: () => void) => {
      clearBrowserImpersonationSession()
      clearImpersonationAdminExitPending()
      setImpersonationExitOverlay(false)
      clearPreImpersonationOidcSnapshot()
      removeCookie('user', userAuthCookieOptions())
      setUser(null)
      setToken(null)
      localStorage.removeItem('contextIri')
      localStorage.removeItem('contextName')
      localStorage.removeItem('switchingTo')
      TrackingService.captureEvent('user_signed_out')
      TrackingService.reset()
      if (callback) {
        await Promise.resolve(callback())
      }
      await getKeycloakUserManager().signoutRedirect()
    },
    [removeCookie, setImpersonationExitOverlay]
  )

  // Only block the app for OIDC bootstrap or the first currentUser fetch (token but no user yet).
  // Do not tie global loading to authUser refetches once we already have a user — that caused
  // RequireAuth + inUserSpace routes to flash a full-screen loader on navigation.
  const blockingAuthLoading =
    !oidcReady || (Boolean(token) && user === null && authUserQueryLoading)

  const value = useMemo(
    () => ({
      user,
      token,
      loading: blockingAuthLoading,
      refreshSession,
      syncOidcAccessToken,
      clearLocalOidcSession,
      signinWithPassword,
      signinWithProvider,
      signin,
      signout,
      isImpersonationExchangeSession: isImpersonationUiActive,
    }),
    [
      user,
      token,
      isImpersonationUiActive,
      blockingAuthLoading,
      refreshSession,
      syncOidcAccessToken,
      clearLocalOidcSession,
      signinWithPassword,
      signinWithProvider,
      signin,
      signout,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
